import { Input, Spin } from "antd";
import type { ChangeEvent, FC } from "react";
import { useCallback } from "react";

import { SecondaryButton } from "../../../Buttons/components/ActionButton";
import { Icon } from "../../../Icon";
import { IconCodes } from "../../../Icon/IconCodes";
import { useLazyLoadConfigValueQuery } from "../../services/server/formComponentsApi";
import type { AntCustomFormField } from "../../types/types";
import { buttonStyle, inputStyle } from "../SiteIdGenerator/buttonGeneratorStyle";

export interface IdGeneratorProps extends AntCustomFormField<string> {
    label: string;
    readOnly: boolean;
    name: string;
    /** The full URL (with query params) used to fetch the generated ID */
    fetchUrl: string;
    /** Name attribute for the generate button */
    buttonName: string;
    /** Whether to wrap the component in a Spin indicator */
    showSpinner?: boolean;
    /** Additional condition to disable the generate button */
    buttonIsDisabled?: boolean;
    inputIsDisabled?: boolean;
}

export const IdGenerator: FC<IdGeneratorProps> = ({
    readOnly,
    onChange,
    fetchUrl,
    buttonName,
    showSpinner = false,
    buttonIsDisabled = false,
    inputIsDisabled = false,
    ...restProps
}) => {
    const onValueChange = (ev: ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
            onChange(ev.target.value);
        }
    };

    const [data, { isFetching }] = useLazyLoadConfigValueQuery();

    const onClick = useCallback(() => {
        if (onChange) {
            data(fetchUrl)
                .unwrap()
                .then((response) => {
                    if (response.value) {
                        onChange(response.value);
                    }
                });
        }
    }, [onChange, fetchUrl, data]);

    const content = (
        <Input.Group compact>
            <Input readOnly={readOnly || isFetching || inputIsDisabled} onChange={onValueChange} style={inputStyle} {...restProps} />
            <SecondaryButton
                name={buttonName}
                onClick={onClick}
                disabled={readOnly || isFetching || buttonIsDisabled}
                icon={<Icon iconName={IconCodes.RedoOutlined} />}
                style={buttonStyle}
            />
        </Input.Group>
    );

    return showSpinner ? <Spin spinning={isFetching}>{content}</Spin> : content;
};
